import { useUser } from '@application/authentication/use_user';
import { CounterState } from '@application/counter';
import { withTranslation } from '@application/i18n/with_translation';
import { IconProps } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { CaseType, Participant } from '@feature/cases/common';
import { useTrackedIssue } from '@feature/issues/use_tracked_issue';
import { BookingParticipantRole, BookingParticipantSubjectRole } from '@visiba/api-vcs-client-v2';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { BottomBanner } from './_bottom_banner.component';
import { ComprehensiveInfo } from './_comprehensive_info.component';
import { ExtensiveInfo } from './_extensive_info.component';
import { IllustrativeSelection } from './_illustrative_selection.component';

interface Props {
	issuesTrackedBy: string;
	to: string;
	participants: Participant[];
	isSelected?: boolean;
	reservedUntil?: CounterState;
	showRfv?: string;
	showLabel?: string;
	shownLabelIcon?: IconProps['type'];
	showBannerText?: string;
	showRepresentedPatient?: string;
	showOtherRepresentatives?: string[];
	currentUserSubjectRole: BookingParticipantSubjectRole | null;
	showTime: VisibaDate;
	type: CaseType;
	showReceptionName: string;
}

const elements = {
	container: styled(Link)`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x1_5};
		width: 100%;
		background: #fff;
		border-radius: 12px;
		padding: ${tokens.spacing.x1_5} ${tokens.spacing.x1_5} ${tokens.spacing.x2};
		position: relative;
		transition: box-shadow 250ms;
		box-shadow: ${tokens.elevation.skim};
		overflow: hidden;

		&:hover {
			box-shadow: ${tokens.elevation.lifted};
		}

		&.is-selected {
			box-shadow: none;
		}
	`,
};

export function CaseCard({
	to,
	issuesTrackedBy,
	isSelected,
	participants,
	reservedUntil,
	showRfv,
	showTime,
	showLabel,
	shownLabelIcon,
	showBannerText,
	showReceptionName,
	showRepresentedPatient,
	showOtherRepresentatives,
	currentUserSubjectRole,
	type,
	...rest
}: Props): JSX.Element {
	const host =
		type === 'messaging'
			? (participants.find((participant) => participant.role === BookingParticipantRole.Host) ?? null)
			: (participants[0] ?? null);

	const issuePendingPayment = useTrackedIssue(issuesTrackedBy, ['awaitPayment']);
	const issueFormsNotSigned = useTrackedIssue(issuesTrackedBy, ['awaitFormResponses']);
	const issueUnreadMessages = useTrackedIssue(issuesTrackedBy, ['unreadMessages']);
	const showIsNew = useTrackedIssue(issuesTrackedBy, ['isNewCase']);

	const user = useUser();

	const getRepresentative = () => {
		const includeCurrentUser =
			currentUserSubjectRole === BookingParticipantSubjectRole.LegalGuardian || showRepresentedPatient != null;
		const names = [...(includeCurrentUser ? [user?.commonName] : []), ...(showOtherRepresentatives ?? [])].filter(
			(name): name is string => name != null && name !== '',
		);

		return names.length > 0 ? names.join(', ') : undefined;
	};

	const representative = getRepresentative();
	const patient =
		showRepresentedPatient ??
		(currentUserSubjectRole === BookingParticipantSubjectRole.MainSubject && showOtherRepresentatives?.length
			? user?.commonName
			: undefined);

	return (
		<elements.container
			aria-current={isSelected ? 'true' : undefined}
			to={to}
			className={classNames({
				'is-selected': isSelected,
			})}
			{...rest}
		>
			<IllustrativeSelection isSelected={!!isSelected} />
			<ExtensiveInfo
				host={host}
				participantAmount={participants.length}
				hasMultipleOtherParticipants={participants.length > 1}
				receptionName={showReceptionName}
				time={showTime}
				representedPatient={patient}
				representative={representative}
				rfv={showRfv}
			/>
			<ComprehensiveInfo
				isNew={showIsNew >= 1}
				label={showLabel}
				labelIcon={shownLabelIcon}
				reservedUntil={reservedUntil}
				pendingPayment={issuePendingPayment}
				unreadMessages={issueUnreadMessages}
				formsNotSigned={issueFormsNotSigned}
				stateText={host == null ? withTranslation('PW.Component.CaseCard.Waiting') : undefined}
			/>
			<BottomBanner>{showBannerText}</BottomBanner>
		</elements.container>
	);
}
